import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { uuidv4 } from '../../../../shared/utils';
import { path } from 'ramda';
import { css } from 'emotion';
import { Input } from '@maxmilhas/hangar-components-atomic';
import RawHtml from '../RawHtml';

const InputFileCss = `
  .file-label {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    height: 48px;
    width: 100%;
    background: #eff7fe;
    border: 1px dashed #5ea2f9;
    color: #9396a7;
    font-weight: 500;
    size: 14px;
    cursor: pointer;
    padding: 12px 48px;
    text-align: center;

    a, a:hover {
      color: #5ea2f9 !important;
      text-decoration: underline !important;
    }
  }

  input {
    display: none;
  }

  .files-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-left: -6px;
    margin-top: 12px;

    &__item {
      display: flex;
      align-items: center;
      min-height: 32px;
      border-radius: 4px;
      padding: 4px 12px;
      margin-left: 6px;
      margin-bottom: 6px;
      background: rgba(0, 0, 0, .12);

      span {
        max-width: 225px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      i {
        cursor: pointer;
        padding: 5px 0 5px 5px;
        margin-left: 8px;
        color: #606060;
      }
    }
  }
`;

export const InputFile = ({
  id, returnOnlyBase64, selectedPlaceholder,
  placeholder, multiple, onChange, value, ...rest
}) => {
  const [files, setFiles] = useState([]);

  const getBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });

  const handleChange = async e => {
    if (path(['target', 'files'], e)) {
      const _files = [...files];

      for (const file of e.target.files) {
        const base64 = await getBase64(file);
        _files.push({
          base64,
          name: file.name,
          size: file.size,
          type: file.type
        });
      }

      if (returnOnlyBase64) {
        onChange(_files.map(file => file.base64));
      } else {
        onChange(_files);
      }

      setFiles(_files);
    }
  };

  const handleRemoveFile = index => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  };

  return (
    <div className={css`${InputFileCss}`}>
      <Input type="file" id={id} multiple={multiple} onChange={handleChange} {...rest} />
      <label className="file-label" htmlFor={id}>
        {files.length > 0 ? (
          <span>
            {files.length} arquivo{files.length > 1 ? 's' : ''} selecionado{files.length > 1 ? 's' : ''}. <RawHtml tag="span">{selectedPlaceholder}</RawHtml>
          </span>
        ) : ( 
          <RawHtml tag="span">{placeholder}</RawHtml>
        )}
      </label>

      {files.length > 0 && (
        <div className="files-wrapper">
          {files.map((file, index) => (
            <div key={uuidv4()} className="files-wrapper__item">
              {file.name.split('.').map((str, i) => <span key={`${str}-${i}`}>{i === 0 ? str : `.${str}`}</span>)}
              <i onClick={() => handleRemoveFile(index)} className="icon-max-navigation-close" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

InputFile.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  returnOnlyBase64: PropTypes.bool,
  selectedPlaceholder: PropTypes.string,
  placeholder: PropTypes.string,
  multiple: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.shape({})),
  onChange: PropTypes.func
};

InputFile.defaultProps = {
  returnOnlyBase64: false,
  selectedPlaceholder: '<a>Clique aqui</a> para adicionar mais arquivos',
  placeholder: '<a>Pesquise</a> ou arraste seus arquivos',
  multiple: false,
  value: null,
  onChange: () => undefined
};

export default InputFile;
